import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../../services/api';

// For static assets
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

const WinnerPage = () => {
  const { slug } = useParams();
  const isBrowser = typeof window !== 'undefined';
  const currentHostname = isBrowser ? window.location.hostname : '';
  
  // Custom domain detection - admin domains show login, custom domains show campaigns
  const isCustomDomain = isBrowser ? 
    !window.location.hostname.includes('gooddrinkspromotion.dailypress.com.au') && 
    !window.location.hostname.includes('localhost') && 
    !window.location.hostname.includes('127.0.0.1') : false;
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [sections, setSections] = useState([]);
  const [overallWinner, setOverallWinner] = useState(null);
  const [winners, setWinners] = useState([]);
  const [error, setError] = useState(null);
  const [resolvedSlug, setResolvedSlug] = useState(slug || '');
  const entryPageLink = isCustomDomain ? '/' : resolvedSlug ? `/campaign/${resolvedSlug}` : '/';

  useEffect(() => {
    if (slug) {
      setResolvedSlug(slug);
    } else if (!isCustomDomain) {
      setResolvedSlug('');
    }
  }, [slug, isCustomDomain]);

  useEffect(() => {
    fetchWinnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isCustomDomain]);

  // Apply custom fonts when campaign data is loaded
  useEffect(() => {
    if (campaign?.font_settings) {
      const fontStyleId = 'campaign-custom-fonts';
      let fontStyleEl = document.getElementById(fontStyleId);
      if (!fontStyleEl) {
        fontStyleEl = document.createElement('style');
        fontStyleEl.id = fontStyleId;
        document.head.appendChild(fontStyleEl);
      }
      
      let fontFaceCSS = '';
      const campaignId = campaign.id || 'default';
      
      // Header font
      if (campaign.font_settings.header_font_file_url) {
        const headerFontUrl = `${ASSET_BASE_URL}${campaign.font_settings.header_font_file_url}`;
        const headerFontName = campaign.font_settings.header_font_name || `CampaignHeaderFont-${campaignId}`;
        const headerFontExt = headerFontUrl.toLowerCase().split('.').pop();
        const headerFontFormat = headerFontExt === 'otf' ? 'opentype' : 'truetype';
        fontFaceCSS += `
          @font-face {
            font-family: '${headerFontName}';
            src: url('${headerFontUrl}') format('${headerFontFormat}');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }
        `;
      }
      
      // Paragraph font
      if (campaign.font_settings.paragraph_font_file_url) {
        const paragraphFontUrl = `${ASSET_BASE_URL}${campaign.font_settings.paragraph_font_file_url}`;
        const paragraphFontName = campaign.font_settings.paragraph_font_name || `CampaignParagraphFont-${campaignId}`;
        const paragraphFontExt = paragraphFontUrl.toLowerCase().split('.').pop();
        const paragraphFontFormat = paragraphFontExt === 'otf' ? 'opentype' : 'truetype';
        fontFaceCSS += `
          @font-face {
            font-family: '${paragraphFontName}';
            src: url('${paragraphFontUrl}') format('${paragraphFontFormat}');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }
        `;
      }
      
      fontStyleEl.textContent = fontFaceCSS;
      
      // Apply fonts to elements
      if (campaign.font_settings.header_font_file_url) {
        const headerFontName = campaign.font_settings.header_font_name || `CampaignHeaderFont-${campaignId}`;
        const headerStyle = document.createElement('style');
        headerStyle.id = 'campaign-header-font-style';
        headerStyle.textContent = `
          h1, h2, h3, h4, h5, h6 {
            font-family: '${headerFontName}', serif !important;
          }
        `;
        // Remove old style if exists
        const oldHeaderStyle = document.getElementById('campaign-header-font-style');
        if (oldHeaderStyle) {
          oldHeaderStyle.remove();
        }
        document.head.appendChild(headerStyle);
      }
      
      if (campaign.font_settings.paragraph_font_file_url) {
        const paragraphFontName = campaign.font_settings.paragraph_font_name || `CampaignParagraphFont-${campaignId}`;
        const paragraphStyle = document.createElement('style');
        paragraphStyle.id = 'campaign-paragraph-font-style';
        paragraphStyle.textContent = `
          p, label, span, div, input, textarea, select, button, li {
            font-family: '${paragraphFontName}', sans-serif !important;
          }
        `;
        // Remove old style if exists
        const oldParagraphStyle = document.getElementById('campaign-paragraph-font-style');
        if (oldParagraphStyle) {
          oldParagraphStyle.remove();
        }
        document.head.appendChild(paragraphStyle);
      }
    }
    
    // Cleanup function
    return () => {
      // Optionally remove font styles when component unmounts
      // But we might want to keep them for other pages, so leaving them
    };
  }, [campaign]);

  const fetchWinnerData = async () => {
    try {
      setLoading(true);
      let response;
      if (slug) {
        response = await api.get(`/public/campaigns/${slug}/winner`);
      } else if (isCustomDomain) {
        if (!currentHostname) {
          setError('Campaign not found or not yet finished');
          setLoading(false);
          return;
        }
        response = await api.get(`/public/campaigns/domain/${encodeURIComponent(currentHostname)}/winner`);
      } else {
        setError('Campaign not found or not yet finished');
        setLoading(false);
        return;
      }
      
      setCampaign(response.data.campaign);
      if (response.data.campaign?.slug) {
        setResolvedSlug(response.data.campaign.slug);
      }
      setSections(response.data.sections || []);
      setOverallWinner(response.data.overallWinner || null);
      setWinners(response.data.winners || []);
    } catch (err) {
      console.error('Error fetching winner page:', err);
      if (err.response?.status === 404) {
        setError('Campaign not found or not yet finished');
      } else {
        setError('Failed to load winner page');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (section) => {
    const sectionClasses = section.full_width_background 
      ? "relative w-full" 
      : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12";

    // Handle background image/video
    const backgroundStyle = section.background_image_url && !section.background_image_url.includes('.mp4') && !section.background_image_url.includes('.webm') && !section.background_image_url.includes('.ogg')
      ? { backgroundImage: `url(${ASSET_BASE_URL}${section.background_image_url})` }
      : {};

    switch (section.type) {
      case 'banner':
        return (
          <div key={section.id} className="pb-6">
            <div className="container mx-auto px-4">
              {section.background_image_url ? (
                section.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                  // Video background
                  <div className="relative h-80 md:h-96 lg:h-[28rem] rounded-lg shadow-lg overflow-hidden">
                    <video
                      className="absolute inset-0 w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                    >
                      <source src={`${ASSET_BASE_URL}${section.background_image_url}`} />
                      Your browser does not support the video tag.
                    </video>
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                    {/* Banner content overlay */}
                    {(section.about_header || section.about_description) && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white z-10 px-4">
                          {section.about_header && (
                            <h1 className="text-4xl md:text-6xl font-bold mb-4">{section.about_header}</h1>
                          )}
                          {section.about_description && (
                            <p className="text-xl md:text-2xl">{section.about_description}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Image background
                  <div 
                    className={`relative h-80 md:h-96 lg:h-[28rem] bg-cover bg-center rounded-lg shadow-lg overflow-hidden ${
                      section.full_width_background ? 'w-full' : ''
                    }`}
                    style={{
                      backgroundColor: campaign?.primary_color || '#1F2937',
                      backgroundImage: `url(${ASSET_BASE_URL}${section.background_image_url})`
                    }}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                    {/* Banner content overlay */}
                    {(section.about_header || section.about_description) && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white z-10 px-4">
                          {section.about_header && (
                            <h1 className="text-4xl md:text-6xl font-bold mb-4">{section.about_header}</h1>
                          )}
                          {section.about_description && (
                            <p className="text-xl md:text-2xl">{section.about_description}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div 
                  className="h-32 rounded-lg shadow-lg flex items-center justify-center"
                  style={{ backgroundColor: campaign?.primary_color || '#1F2937' }}
                >
                  {/* Banner content for no background */}
                  {(section.about_header || section.about_description) && (
                    <div className="text-center text-white px-4">
                      {section.about_header && (
                        <h1 className="text-2xl md:text-3xl font-bold">{section.about_header}</h1>
                      )}
                      {section.about_description && (
                        <p className="text-lg mt-2">{section.about_description}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'content':
        // Check template to determine layout
        if (section.template_codename === 'content-without-background') {
          // Content without background - simple centered layout matching banner width
          return (
            <section key={section.id} className="pb-6">
              <div className="container mx-auto px-4">
                <div className="p-8 md:p-12">
                  {section.about_header && (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: campaign?.primary_color || '#1F2937' }}>
                      {section.about_header?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!')}
                    </h2>
                  )}
                  {section.about_description && (
                    <div className="prose prose-lg mx-auto">
                      <div className="text-base md:text-lg lg:text-xl text-center" dangerouslySetInnerHTML={{ __html: section.about_description?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!') }} />
                    </div>
                  )}
                  
                  {/* Display selected winner */}
                  {(overallWinner || winners.length > 0) && (
                    <div className="mt-12">
                      {renderWinners()}
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        } else if (section.template_codename === 'content-with-background-overlay') {
          // Content with Background Overlay template - Full width with overlay
          return (
            <section key={section.id} className="relative py-0">
              {section.background_image_url ? (
                <>
                  {section.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                    // Video background with overlay
                    <div className="relative h-[600px] md:h-[700px] lg:h-[800px] overflow-hidden">
                      <video
                        className="absolute inset-0 w-full h-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                      >
                        <source src={`${ASSET_BASE_URL}${section.background_image_url}`} />
                        Your browser does not support the video tag.
                      </video>
                      <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                      <div className="relative h-full flex items-center justify-center px-6 md:px-8 lg:px-12">
                        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-4xl mx-auto">
                          {section.about_header && (
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: campaign?.primary_color || '#1F2937' }}>
                              {section.about_header?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!')}
                            </h2>
                          )}
                          {section.about_description && (
                            <div className="prose prose-lg mx-auto">
                              <div className="text-base md:text-lg lg:text-xl text-center" dangerouslySetInnerHTML={{ __html: section.about_description?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!') }} />
                            </div>
                          )}
                          {/* Display selected winner */}
                          {(overallWinner || winners.length > 0) && (
                            <div className="mt-8">
                              {renderWinners()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Image background with overlay
                    <div 
                      className="relative h-[600px] md:h-[700px] lg:h-[800px] bg-cover bg-center"
                      style={{ backgroundImage: `url(${ASSET_BASE_URL}${section.background_image_url})` }}
                    >
                      <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                      <div className="relative h-full flex items-center justify-center px-6 md:px-8 lg:px-12">
                        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-4xl mx-auto">
                          {section.about_header && (
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: campaign?.primary_color || '#1F2937' }}>
                              {section.about_header?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!')}
                            </h2>
                          )}
                          {section.about_description && (
                            <div className="prose prose-lg mx-auto">
                              <div className="text-base md:text-lg lg:text-xl text-center" dangerouslySetInnerHTML={{ __html: section.about_description?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!') }} />
                            </div>
                          )}
                          {/* Display selected winner */}
                          {winners.length > 0 && (
                            <div className="mt-8">
                              {renderWinners()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Fallback if no background image
                <div className="py-16 bg-gray-900">
                  <div className="container mx-auto px-4">
                    <div className="text-center max-w-4xl mx-auto">
                      <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white">
                        {section.about_header || 'About This Campaign'}
                      </h2>
                      <div className="prose prose-xl prose-invert mx-auto">
                        <div className="text-xl md:text-2xl lg:text-3xl text-white" dangerouslySetInnerHTML={{ __html: section.about_description }} />
                      </div>
                      {/* Display selected winner */}
                      {(overallWinner || winners.length > 0) && (
                        <div className="mt-8">
                          {renderWinners()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
          );
        } else if (section.background_image_url) {
          // Content with Background template (side-by-side layout)
          return (
            <section key={section.id} className="py-12">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch" style={{ minHeight: '500px' }}>
                  {/* Left Column: Content */}
                  <div className="flex items-center justify-center">
                    <div className="text-center max-w-lg mx-auto">
                      {section.about_header && (
                        <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
                          {section.about_header?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!')}
                        </h2>
                      )}
                      {section.about_description && (
                        <div className="prose prose-xl mx-auto">
                          <div className="whitespace-pre-wrap text-lg md:text-xl" dangerouslySetInnerHTML={{ __html: section.about_description }} />
                        </div>
                      )}
                      {/* Display selected winner */}
                      {(overallWinner || winners.length > 0) && (
                        <div className="mt-8">
                          {renderWinners()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Background Image */}
                  <div className="h-full">
                    {section.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                      // Video background
                      <div className="relative h-full min-h-[500px] rounded-lg shadow-lg overflow-hidden">
                        <video
                          className="w-full h-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                        >
                          <source src={`${ASSET_BASE_URL}${section.background_image_url}`} />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : (
                      // Image background
                      <div className="relative h-full min-h-[500px] rounded-lg shadow-lg overflow-hidden">
                        <img
                          src={`${ASSET_BASE_URL}${section.background_image_url}`}
                          alt={section.about_header || 'Content background'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        } else {
          // Default content layout (original)
          return (
            <section key={section.id} className="py-16">
              <div className="container mx-auto px-4">
                <div className="rounded-2xl shadow-lg p-8 md:p-12 max-w-4xl mx-auto">
                  {section.about_header && (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center" style={{ color: campaign?.primary_color || '#1F2937' }}>
                      {section.about_header?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!')}
                    </h2>
                  )}
                  {section.about_description && (
                    <div className="prose prose-lg mx-auto">
                      <div className="text-base md:text-lg lg:text-xl text-center" dangerouslySetInnerHTML={{ __html: section.about_description?.replace(/Win 1 of 3 Trips/g, 'Win a Trip').replace(/lucky winners below/g, 'lucky winner below').replace(/has ended\. View the lucky winner below!/g, 'has ended. Congratulations to our winner!')
.replace(/View the lucky winner below!/g, 'Congratulations to our winner!')
.replace(/lucky winner below!/g, 'our winner!') }} />
                    </div>
                  )}
                  
                  {/* Display winners based on winner_display_type */}
                  {section.winner_display_type && section.winner_display_type !== 'none' && (
                    <div className="mt-8">
                      {renderWinners(section.winner_display_type)}
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        }

      case 'faqs':
        return (
          <div key={section.id} className={sectionClasses}>
            <div className="max-w-3xl mx-auto">
              {section.faqs_header && (
                <h2 className="text-3xl font-bold text-gray-900 text-center mb-6">{section.faqs_header}</h2>
              )}
              {section.faqs_description && (
                <div 
                  className="text-center mb-12 prose prose-lg"
                  dangerouslySetInnerHTML={{ __html: section.faqs_description }}
                />
              )}
              <div className="space-y-4">
                {section.faqs?.map((faq, index) => (
                  <details key={faq.id || index} className="bg-white rounded-lg shadow-md p-6">
                    <summary className="font-semibold text-lg cursor-pointer text-gray-900">
                      {faq.question}
                    </summary>
                    <div className="mt-4 text-gray-700 prose" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </details>
                ))}
              </div>
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div key={section.id} className={sectionClasses}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.gallery?.map((item, index) => (
                <div key={item.id || index} className="relative group overflow-hidden rounded-lg shadow-lg">
                  <img 
                    src={`${ASSET_BASE_URL}${item.image_url}`}
                    alt={item.title || `Gallery image ${index + 1}`}
                    className="w-full h-64 object-cover transition-transform group-hover:scale-110"
                  />
                  {(item.title || item.description) && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-75 transition-opacity flex items-end">
                      <div className="p-4 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        {item.title && <h3 className="font-bold text-lg">{item.title}</h3>}
                        {item.description && <p className="text-sm mt-1">{item.description}</p>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderWinners = () => {
    const winnerToDisplay = overallWinner || winners[0];

    if (!winnerToDisplay) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Congratulations!</h3>
          <p className="text-gray-600">The winner has been selected and will be announced soon.</p>
        </div>
      );
    }

    // Display the single winner prominently
    const winner = winnerToDisplay;
    return (
      <div className="text-center">
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-3xl p-8 md:p-12 border-4 border-yellow-400 shadow-2xl max-w-2xl mx-auto">
          <div className="text-8xl mb-6">🏆</div>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Congratulations!
          </h3>
          <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {winner.name || winner.user_name || 'Winner'}
            </h4>
            {winner.email && (
              <p className="text-lg text-gray-600 mb-4">{winner.email}</p>
            )}
            <div className="inline-flex items-center px-6 py-3 bg-yellow-400 text-yellow-900 font-bold text-lg rounded-full">
              🎉 Grand Prize Winner 🎉
            </div>
          </div>
          <p className="text-gray-600 text-sm">
            Thank you for participating in our campaign!
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error}</h1>
          <Link to={entryPageLink} className="text-primary hover:underline">
            Return to homepage
          </Link>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Custom CSS */}
      {campaign.custom_css && (
        <style dangerouslySetInnerHTML={{ __html: campaign.custom_css }} />
      )}

      {/* Header with campaign theme */}
      <header className="bg-gray-50">
        {/* Logo Section */}
        <div className="pt-6 pb-4">
          <div className="container mx-auto px-4">
            {campaign?.logo_url && (
              <img 
                src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                alt={campaign.name}
                className="h-16 md:h-20"
              />
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="bg-gray-50">
        {/* Render sections */}
        {sections.length > 0 ? (
          sections.map(section => renderSection(section))
        ) : (
          // Fallback if no sections configured
          <div className="container mx-auto px-4 py-20">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {campaign.name} - Winner Announced!
              </h1>
              {renderWinners()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default WinnerPage;
